import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isNil, noop } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import {
    Routes,
    navigateToWorkbench,
    navigateToHomePageTable,
    navigateToUsageReportingPage
} from 'app/routes/routes';
import { ViewEntityPropType, FullyQualifiedNamePropType } from 'app/lib/proptypes';
import { isDatasetsList, isWorkbench, isUsageReportingPage } from 'app/routes';
import { splitFqn, toStringFqn } from 'app/lib/utils';
import { Snackbar, SnackbarMessage, SnackbarVariant } from 'app/components/core/Snackbar';
import { ShowDetailsButton } from 'app/components/features/NotificationHub/ShowDetailsButton.jsx';
import { EXPLORATION_DOWNLOAD_FAILED } from 'app/store/exploreData/types.js';

export function ExploreDataNotification({
    id,
    onDismiss,
    entityId,
    entity = null,
    queryUuid,
    type,
    selectedWorkbenchDatasetQueryUuid = null,
    selectedEntityId = null,
    currentFqn = null,
    currentTab = null,
    currentUsageReportingTab = null,
    pathname = null,
    onSelectDatasetTab = noop,
    onSelectWorkbenchDatasetTab = noop,
    onSelectUsageReportingTab = noop
}) {
    const { t } = useTranslation();
    const isFailedNotification = type === EXPLORATION_DOWNLOAD_FAILED;
    useEffect(() => {
        if (
            (isWorkbench() &&
                selectedWorkbenchDatasetQueryUuid === queryUuid &&
                entityId === selectedEntityId) ||
            (isDatasetsList() &&
                currentTab === queryUuid &&
                entityId === toStringFqn(currentFqn)) ||
            (isUsageReportingPage() && currentUsageReportingTab === queryUuid)
        ) {
            onDismiss(id);
        }
    }, [
        id,
        selectedWorkbenchDatasetQueryUuid,
        queryUuid,
        entityId,
        selectedEntityId,
        onDismiss,
        currentTab,
        currentFqn,
        currentUsageReportingTab
    ]);

    const fqn = !entity ? splitFqn(entityId) : null;

    if (!entity && isNil(fqn)) {
        return null;
    }

    const handleClickDetails = () => {
        if (pathname === Routes.DATASETS) {
            onSelectDatasetTab(queryUuid);
            navigateToHomePageTable(toStringFqn(fqn));
        } else if (pathname === Routes.USAGE_REPORTING) {
            onSelectUsageReportingTab(queryUuid);
            navigateToUsageReportingPage();
        } else if (entity) {
            const { entityId, spaceSlug, uid } = entity;
            onSelectWorkbenchDatasetTab(queryUuid, entityId);
            navigateToWorkbench(spaceSlug, uid);
        }
    };

    const name = entity ? entity.name : fqn.table;
    return (
        <Snackbar
            id={id}
            onDismiss={onDismiss}
            autoHideEnabled={!isFailedNotification}
            variant={isFailedNotification ? SnackbarVariant.ERROR : SnackbarVariant.SUCCESS}>
            <SnackbarMessage>{t(`notifications:${type}`, { name })}</SnackbarMessage>
            <ShowDetailsButton
                i18nKey="notifications:exploreDataDetailsButton"
                id={id}
                onClick={handleClickDetails}
            />
        </Snackbar>
    );
}

ExploreDataNotification.propTypes = {
    id: PropTypes.string.isRequired,
    entity: ViewEntityPropType,
    entityId: PropTypes.string.isRequired,
    queryUuid: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    onDismiss: PropTypes.func.isRequired,
    selectedWorkbenchDatasetQueryUuid: PropTypes.string,
    selectedEntityId: PropTypes.string,
    currentFqn: FullyQualifiedNamePropType,
    currentTab: PropTypes.string,
    currentUsageReportingTab: PropTypes.string,
    pathname: PropTypes.string,
    onSelectDatasetTab: PropTypes.func,
    onSelectWorkbenchDatasetTab: PropTypes.func,
    onSelectUsageReportingTab: PropTypes.func
};
